import React, { useRef } from 'react';
import { connect } from 'react-redux';

import styled from 'styled-components';

import amplitude from 'amplitude-js';
import { toggleSpinner, toggleModal, toggleModalWithTimer } from 'apps/auth/actions/appAction';
import { toggleChat } from 'apps/auth/actions/chatAction';
import { addExtras } from 'apps/auth/actions/extraAction';
import { updatePageMessages } from 'apps/auth/actions/messageAction';
import { DangerouslyHtml } from 'apps/auth/components/common/DangerouslyHtml/DangerouslyHtml';
import { DiskBadge } from 'apps/auth/components/common/DiskBadge/DiskBadge';
import { HeaderPanel } from 'apps/auth/components/common/Layout/HeaderPanel/HeaderPanel';
import { Separator } from 'apps/auth/components/common/Layout/Separator';
import { Panel } from 'apps/auth/components/common/Panel/Panel';
import { DiskBadgeWrapper, SelectedDiskBadge } from 'apps/auth/components/common/Panel/styles';
import PopupNew from 'apps/auth/components/common/Popup/PopupNew';
import { Text } from 'apps/auth/components/common/Text/Text';
import IbanAccountDetailsModal from 'apps/auth/components/ga/accounts/modals/IbanAccountDetailsModal';
import OnfidoWaitingStatus from 'apps/auth/components/ga/kyc/KYCOnfidoStatus/OnfidoWaitingStatus';
import { GA_TOP_UP_METHOD_SELECTED } from 'apps/auth/constants/amplitude/AmplitudeEvents';
import { PAYMENT_METHOD_AMPLITUDE } from 'apps/auth/constants/amplitude/AmplitudeParamName';
import { topUpMethods } from 'apps/auth/constants/amplitude/AmplitudeParamValues';
import { URL_IMAGES_I, AUTH_GA_POCKET } from 'apps/auth/constants/UrlConstants';
import { getBankAlias, isObject, resolveArguments } from 'apps/auth/helpers/utils';
import { usePageMessages, useExtra } from 'apps/auth/hooks/selectors';
import { requestForReceivingIban, showPopupForCorrectAddress } from 'apps/auth/services/GaAccountsService';
import media from 'apps/auth/theme/media';

const GoogleImg = styled.img`
    max-width: 40px;

    ${media.desktop1920} {
        max-width: 56px;
    }
`;

const KYC_LEVEL_TWO = 2;

const RenderLink = ({ href, onClickHandler = null, message, iconName = 'bank' }) => {
    return (
        <Panel type="link" href={href} onClick={onClickHandler} isHoverable negativeMargin>
            <DiskBadgeWrapper>
                <DiskBadge variant="secondary" iconName={iconName} />
            </DiskBadgeWrapper>
            {message}
        </Panel>
    );
};

const TopUpMethodList = (props) => {
    const extra = useExtra();
    const messages = usePageMessages();
    const popupRef = useRef(null);
    const getActualTitle = (amount, currency) => {
        if (amount !== undefined && currency !== undefined && amount > 0 && currency !== '') {
            return resolveArguments(messages.titleWithParams, [amount, currency]);
        }
        return messages.title;
    };

    const amplitudeTopUpEvent = (method) => {
        amplitude.getInstance().logEvent(GA_TOP_UP_METHOD_SELECTED, {
            [PAYMENT_METHOD_AMPLITUDE]: method,
        });
    };

    const getAmount = () => {
        return props.amount ? '?amount=' + props.amount : '';
    };

    const getCardItem = (card) => {
        const link = `${AUTH_GA_POCKET}${extra.id}/top-up/card/${card.id}${getAmount()}`;
        return (
            <Panel
                type="link"
                href={link}
                isHoverable
                negativeMargin
                key={card.id}
                onClick={() => {
                    amplitudeTopUpEvent(topUpMethods.BANK_CARD);
                }}
            >
                <DiskBadgeWrapper>
                    {getBankAlias(card.cardLogo)}
                    {props.currentCard === card.id && (
                        <SelectedDiskBadge>
                            <DiskBadge variant="selectedPassive" iconName="check" />
                        </SelectedDiskBadge>
                    )}
                </DiskBadgeWrapper>
                {card.alias} ·· {card.description}
            </Panel>
        );
    };

    const onClickHandlerBySepa = (e) => {
        amplitudeTopUpEvent(topUpMethods.BANK_TRANSFER);
        if (!extra.account?.bic && extra.currentLevel >= KYC_LEVEL_TWO) {
            e.preventDefault();
            requestForReceivingIban(
                extra.id,
                {
                    toggleSpinner: props.toggleSpinner,
                    toggleModal: props.toggleModal,
                    toggleModalWithTimer: props.toggleModalWithTimer,
                    updatePageMessages: props.updatePageMessages,
                    addExtras: props.addExtras,
                    popupRef,
                },
                <IbanAccountDetailsModal />,
                true,
                messages.accountDetailsIssued,
            );
        }
        if (extra.isWaitingStatus && extra.currentLevel < KYC_LEVEL_TWO) {
            e.preventDefault();
            props.toggleModal(true, <OnfidoWaitingStatus />);
        }
    };
    const getSepaHref = () => {
        let href = '#';
        if (!extra.account?.bic && extra.currentLevel < KYC_LEVEL_TWO) {
            href = extra.linkKyc;
        }
        if (extra.account?.bic) {
            href = `${AUTH_GA_POCKET}${extra.id}/top-up/account-details`;
        }
        return href;
    };

    const onClickHandlerBankTransfer = (e) => {
        amplitudeTopUpEvent(topUpMethods.BANK_TRANSFER);
        if (extra.IsBritishAccountInGbp && !extra.account.accountDetails?.number) {
            e.preventDefault();
            showPopupForCorrectAddress(popupRef, props.toggleChat, messages);
        }
    };

    return (
        <>
            <HeaderPanel title={getActualTitle(props.amount, props.currency)} />
            {extra.canTopUpBySepa && <RenderLink href={getSepaHref()} onClickHandler={onClickHandlerBySepa} message={messages.bankTransfer} />}
            {extra.canUseBankTransferMethod && (
                <RenderLink
                    href={`${AUTH_GA_POCKET}${extra.id}/top-up/bank-transfer`}
                    onClickHandler={onClickHandlerBankTransfer}
                    message={messages.bankTransfer}
                />
            )}
            {extra.canTopUpViaPlaid && (
                <RenderLink
                    href={`${AUTH_GA_POCKET}${extra.id}/top-up/plaid${getAmount()}`}
                    iconName="bank-zap"
                    onClickHandler={() => {
                        amplitudeTopUpEvent(topUpMethods.BANK_TRANSFER);
                    }}
                    message={
                        <div>
                            <div>{messages.bankAccount}</div>
                            <DangerouslyHtml type="text" size="secondary" variant="div" color="secondary" dangerouslyHtml={messages.bankAccountText} />
                        </div>
                    }
                />
            )}
            {extra.canTopUpViaTrustly && (
                <RenderLink
                    href={`${AUTH_GA_POCKET}${extra.id}/top-up/trustly${getAmount()}`}
                    iconName="bank-zap"
                    onClickHandler={() => {
                        amplitudeTopUpEvent(topUpMethods.BANK_TRANSFER);
                    }}
                    message={
                        <div>
                            <div>{messages.bankAccount}</div>
                            <DangerouslyHtml type="text" size="secondary" variant="div" color="secondary" dangerouslyHtml={messages.bankAccountText} />
                        </div>
                    }
                />
            )}
            {extra.canTopUpByGooglePay && (
                <Panel
                    type="link"
                    href={`${AUTH_GA_POCKET}${extra.id}/top-up/google-pay`}
                    isHoverable
                    negativeMargin
                    onClick={() => {
                        amplitudeTopUpEvent(topUpMethods.GOOGLE_PAY);
                    }}
                >
                    <DiskBadgeWrapper>
                        <GoogleImg src={`${URL_IMAGES_I}/google-pay-logo-56.png`} alt="Google Pay" />
                    </DiskBadgeWrapper>
                    Google Pay
                </Panel>
            )}
            {(!isObject(extra.linkedCards) || (Array.isArray(extra.linkedCards) && extra.linkedCards.length === 0)) && (
                <RenderLink
                    href={`${AUTH_GA_POCKET}${extra.id}/top-up/card`}
                    iconName="card"
                    onClickHandler={() => {
                        amplitudeTopUpEvent(topUpMethods.BANK_CARD);
                    }}
                    message={
                        <div>
                            <div>{messages.card}</div>
                            <DangerouslyHtml type="text" size="secondary" variant="div" color="secondary" dangerouslyHtml={messages.cardText} />
                        </div>
                    }
                />
            )}
            {Array.isArray(extra.linkedCards) && extra.linkedCards.length > 0 && (
                <>
                    <Separator marginBottom="48px" />
                    <Text variant="div" size="subheader" color="secondary" indent="adaptive-24-32">
                        {messages.linkedCardsTitle}
                    </Text>
                    <Panel
                        type="link"
                        href={`${AUTH_GA_POCKET}${extra.id}/top-up/card`}
                        isHoverable
                        negativeMargin
                        onClick={() => {
                            amplitudeTopUpEvent(topUpMethods.BANK_CARD);
                        }}
                    >
                        <DiskBadgeWrapper>
                            <DiskBadge variant="transparent" iconName="plus" />
                        </DiskBadgeWrapper>
                        <Text color="primary">{messages.addCard}</Text>
                    </Panel>
                    {extra.linkedCards.map((card) => getCardItem(card))}
                </>
            )}
            <PopupNew ref={popupRef} />
        </>
    );
};
export default connect(null, {
    toggleModal,
    toggleSpinner,
    toggleModalWithTimer,
    updatePageMessages,
    addExtras,
    toggleChat,
})(TopUpMethodList);
